import { Component, OnDestroy, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { ApiService } from '../services/api.service';
import { AuthService } from '../services/auth.service';
import {
  Campaign,
  CampaignOperation,
  DataSource,
  FlowNode,
  Lead,
  OperationsStatus,
} from '../models/types';
import { formatDuration } from '../utils/duration';
import { completionLabel } from '../utils/call-completion';

const NODE_TYPES = [
  'Start',
  'Greeting',
  'SpeakScript',
  'RagAnswer',
  'DbLookup',
  'CollectForm',
  'PushFormApi',
  'Reschedule',
  'TransferOrEnd',
  'HumanGate',
];

@Component({
  standalone: true,
  imports: [CommonModule, FormsModule, RouterLink],
  template: `
    <div class="page" *ngIf="campaign() as c">
      <div class="top">
        <div>
          <a routerLink="/campaigns" class="ux-caption">← All agents</a>
          <h1>{{ c.name }}</h1>
          <p>{{ c.campaign_type }} · {{ c.status }}</p>
        </div>
        <div class="actions">
          <button
            *ngIf="isAdmin() && step === 2"
            [disabled]="statusBusy"
            (click)="setStatus('active')"
          >
            {{
              c.campaign_type === 'inbound'
                ? 'Save & activate receptionist'
                : 'Save & start campaign'
            }}
          </button>
          <button class="ghost" *ngIf="isAdmin()" (click)="setStatus('paused')">Pause</button>
          <button (click)="save()">Save</button>
        </div>
      </div>

      <nav class="row" aria-label="Campaign setup">
        <button class="ghost" (click)="step = 0" [attr.aria-current]="step === 0 ? 'step' : null">
          1. Script & settings
        </button>
        <button class="ghost" (click)="step = 1" [attr.aria-current]="step === 1 ? 'step' : null">
          2. {{ c.campaign_type === 'outbound' ? 'Add contacts' : 'Inbound routing' }}
        </button>
        <button
          class="ghost"
          (click)="step = 2; refreshExecution()"
          [attr.aria-current]="step === 2 ? 'step' : null"
        >
          3. Review & start
        </button>
      </nav>
      <p class="ok" role="status" *ngIf="saved()">Campaign saved.</p>
      <p class="err" role="alert" *ngIf="error()">{{ error() }}</p>
      <section class="card" *ngIf="step === 1 && c.campaign_type === 'inbound'">
        <h3>Route incoming calls</h3>
        <p>
          This receptionist does not need a contact list. Test it first, then have your operator
          confirm the production route. Activating the agent alone does not assign its route.
        </p>
        <a class="ux-link-button" routerLink="/test-call" [queryParams]="{ campaign: c.id }"
          >Test this receptionist ↗</a
        >
        <details>
          <summary>Production route reference</summary>
          <p>Campaign ID: {{ c.id }}</p>
        </details>
      </section>
      <section class="card execution" [hidden]="step !== 2">
        <div class="execution-head">
          <div>
            <h3>Readiness & activity</h3>
            <span class="updated" *ngIf="operations() as ops">
              Updated {{ ops.refreshed_at | date: 'mediumTime' }}
            </span>
          </div>
          <button
            type="button"
            class="ghost"
            (click)="refreshExecution()"
            [disabled]="queueLoading()"
          >
            {{ queueLoading() ? 'Refreshing…' : 'Refresh status' }}
          </button>
        </div>
        <p class="help">
          <strong>SIP edge:</strong> {{ operations()?.dialer?.message || 'Checking…' }}
        </p>
        <ng-container *ngIf="operation() as op">
          <div class="state-row">
            <span class="state" [attr.data-state]="op.execution_state">{{
              op.execution_state
            }}</span>
            <span>{{ op.running_calls }} live</span>
            <span *ngIf="c.campaign_type === 'outbound'"
              >{{ op.lead_counts['pending'] || 0 }} pending</span
            >
            <span *ngIf="c.campaign_type === 'outbound'"
              >{{ op.lead_counts['scheduled'] || 0 }} scheduled</span
            >
            <span *ngIf="c.campaign_type === 'outbound'"
              >{{ op.lead_counts['completed'] || 0 }} completed</span
            >
            <span>{{ op.total_calls }} total calls</span>
          </div>
          <div class="progress" *ngIf="c.campaign_type === 'outbound'">
            <span [style.width.%]="op.completed_percent"></span>
          </div>
          <p class="hint" *ngIf="c.campaign_type === 'outbound'">
            {{ op.completed_percent }}% finished · calling window {{ op.window }} {{ op.timezone }}
          </p>
          <ul class="blockers" *ngIf="op.blockers.length">
            <li *ngFor="let b of op.blockers">{{ b }}</li>
          </ul>

          <h4 *ngIf="op.live_calls.length" class="live-heading">
            <span class="pulse" aria-hidden="true"></span>
            Live now
          </h4>
          <div class="live-item" *ngFor="let call of op.live_calls" [attr.data-phase]="call.phase">
            <div class="live-item-head">
              <div>
                <strong>{{ call.lead_name || 'Unknown' }}</strong>
                <span>{{ call.phone || '—' }}</span>
              </div>
              <div class="live-item-aside">
                <span class="phase" [attr.data-phase]="call.phase">{{ call.talking }}</span>
                <span class="duration">{{ formatDuration(call.duration_seconds) }}</span>
              </div>
            </div>
            <p class="snippet" *ngIf="call.last_text">
              <em>{{ call.last_speaker }}:</em> {{ call.last_text }}
            </p>
          </div>

          <h4>Recent calls</h4>
          <div
            class="history-item"
            *ngFor="let call of op.recent_calls"
            [attr.data-status]="normalizeStatus(call.status)"
          >
            <div class="history-head">
              <div>
                <strong>{{ call.lead_name || call.phone || 'Unknown number' }}</strong>
                <span
                  >{{ call.phone }} · {{ call.direction }} ·
                  {{ call.started_at | date: 'short' }}</span
                >
              </div>
              <div class="history-aside">
                <span class="status-pill" [attr.data-status]="normalizeStatus(call.status)">
                  {{ completionLabel(call.completion_state) }}
                </span>
                <span class="duration">{{ formatDuration(call.duration_seconds) }}</span>
              </div>
            </div>
            <p *ngIf="call.completion_reason">{{ call.completion_reason }}</p>
            <p *ngIf="call.retry_at">Retry eligible: {{ call.retry_at | date: 'medium' }}</p>
            <p class="summary" *ngIf="call.summary">{{ call.summary }}</p>
          </div>
          <p class="hint" *ngIf="!op.recent_calls.length">No calls recorded yet.</p>
        </ng-container>
      </section>

      <section class="grid" [hidden]="step !== 0">
        <div class="card">
          <h3>Script & voice</h3>
          <label>Language</label>
          <input [(ngModel)]="c.language" />
          <label>Gemini voice</label>
          <input [(ngModel)]="c.gemini_voice" />
          <label>Script</label>
          <textarea rows="8" [(ngModel)]="c.script_text"></textarea>
          <label>Human gate prompt</label>
          <input [(ngModel)]="c.human_gate_prompt" />
          <label class="check">
            <input type="checkbox" [(ngModel)]="c.human_gate_enabled" />
            Enable human-answer gate (outbound)
          </label>
          <label>Max call length (minutes)</label>
          <input type="number" min="1" max="30" [(ngModel)]="c.max_call_minutes" />
          <p class="hint">
            The agent is told to wrap up one minute before this and always say a proper goodbye
            (اللہ حافظ). The phone line is only cut after the goodbye, or at the hard limit.
          </p>
        </div>

        <div class="card">
          <h3>Retry / window</h3>
          <label>Retry after (minutes)</label>
          <input type="number" [(ngModel)]="c.retry_after_minutes" />
          <label>Max attempts</label>
          <input type="number" [(ngModel)]="c.max_attempts" />
          <label>Window start</label>
          <input [(ngModel)]="c.window_start" />
          <label>Window end</label>
          <input [(ngModel)]="c.window_end" />
          <label>Timezone</label>
          <input [(ngModel)]="c.timezone" />
          <label>Data source</label>
          <select [(ngModel)]="c.data_source_id">
            <option [ngValue]="null">None</option>
            <option *ngFor="let d of dataSources()" [ngValue]="d.id">{{ d.name }}</option>
          </select>
          <label>Callback campaign id (inbound → outbound)</label>
          <input [(ngModel)]="c.callback_campaign_id" />
        </div>
      </section>

      <section class="card" [hidden]="step !== 0">
        <h3>Flow builder</h3>
        <p class="hint">
          Build the call steps in order. Important form nodes:
          <strong>CollectForm</strong> = AI asks the caller for the fields listed below.
          <strong>PushFormApi</strong> = after fields are collected, POST them to your API (or the
          demo inbox). Put CollectForm before PushFormApi.
        </p>
        <div class="flow">
          <div class="node" *ngFor="let n of c.flow_json.nodes; let i = index">
            <select [(ngModel)]="n.type" (ngModelChange)="onNodeTypeChange(n)">
              <option *ngFor="let t of nodeTypes" [value]="t">{{ t }}</option>
            </select>
            <input [(ngModel)]="n.label" placeholder="Label" />
            <button class="ghost" (click)="removeNode(i)">Remove</button>
          </div>
        </div>
        <div class="row">
          <button class="ghost" (click)="addNode()">Add node</button>
          <button class="ghost" (click)="addFormFlowExample()">
            Add CollectForm + PushFormApi example
          </button>
        </div>
      </section>

      <section class="card" [hidden]="step !== 0">
        <h3>Knowledge files</h3>
        <input #knowledgeInput type="file" (change)="onFile($event)" />
        <ul class="file-list">
          <li *ngFor="let f of files()">
            <span>{{ f.filename }} ({{ f.chars }} chars)</span>
            <button type="button" class="ghost danger" (click)="removeFile(f.id)">Remove</button>
          </li>
        </ul>
        <p class="hint" *ngIf="!files().length">No knowledge files attached.</p>
      </section>

      <section class="card" *ngIf="c.campaign_type === 'outbound'" [hidden]="step !== 1">
        <h3>CSV lead import</h3>
        <div class="row">
          <input #csvInput type="file" accept=".csv,text/csv" (change)="onCsv($event)" />
          <button
            type="button"
            class="ghost"
            *ngIf="csvFile"
            (click)="clearCsv(csvInput)"
            [disabled]="importing()"
          >
            Clear file
          </button>
        </div>
        <p class="hint" *ngIf="csvFile">Selected: {{ csvFile.name }}</p>
        <div class="map" *ngIf="csvHeaders().length">
          <label>Phone column</label>
          <select [(ngModel)]="phoneCol" [disabled]="importing()">
            <option *ngFor="let h of csvHeaders()" [value]="h">{{ h }}</option>
          </select>
          <label>Name column</label>
          <select [(ngModel)]="nameCol" [disabled]="importing()">
            <option value="">(none)</option>
            <option *ngFor="let h of csvHeaders()" [value]="h">{{ h }}</option>
          </select>
          <label>Address column</label>
          <select [(ngModel)]="addressCol" [disabled]="importing()">
            <option value="">(none)</option>
            <option *ngFor="let h of csvHeaders()" [value]="h">{{ h }}</option>
          </select>
          <label>Gender column <em>(male / female — required for correct address)</em></label>
          <select [(ngModel)]="genderCol" [disabled]="importing()">
            <option value="">(none — set per lead after import)</option>
            <option *ngFor="let h of csvHeaders()" [value]="h">{{ h }}</option>
          </select>
          <label
            >Additional info / insights column
            <em>(optional — CRM notes about the customer)</em></label
          >
          <select [(ngModel)]="insightsCol" [disabled]="importing()">
            <option value="">(none)</option>
            <option *ngFor="let h of csvHeaders()" [value]="h">{{ h }}</option>
          </select>
          <button type="button" (click)="importCsv(csvInput)" [disabled]="importing() || !csvFile">
            {{ importing() ? 'Importing…' : 'Import' }}
          </button>
        </div>
        <p class="hint">
          Map a Gender column (values like male/female/m/f). The agent must respect saved gender
          when speaking (صاحب vs میڈم). Map Additional info when the CSV has insight/notes about the
          school or contact — the agent will use it on the call. You can also set gender on each
          lead below.
        </p>
        <p *ngIf="importMsg()">{{ importMsg() }}</p>
        <h4>Leads ({{ leads().length }})</h4>
        <div class="lead-reset-actions">
          <button type="button" (click)="openReset('selected')" [disabled]="!selectedLeadIds().size || resettingLeads()">Reset selected ({{ selectedLeadIds().size }})</button>
          <button type="button" (click)="openReset('all')" [disabled]="resettingLeads()">Reset all campaign contacts</button>
          <label><input type="checkbox" [checked]="allVisibleSelected()" (change)="selectVisibleLeads($event)" [disabled]="resettingLeads()" /> Select visible contacts</label>
        </div>
        <div class="reset-review" *ngIf="resetScope() as scope" role="region" aria-label="Review contact reset">
          <strong>{{ scope === 'all' ? 'Start a new run for all eligible contacts in this campaign?' : 'Start a new run for ' + selectedLeadIds().size + (selectedLeadIds().size === 1 ? ' selected contact?' : ' selected contacts?') }}</strong>
          <p>Attempt counters restart at zero. Previous calls and recordings remain in history. Active calls and suppressed numbers are skipped.</p>
          <p *ngIf="c.status === 'active'">This campaign is active: calling may resume immediately within its calling window.</p>
          <button type="button" (click)="confirmLeadReset()" [disabled]="resettingLeads()">{{ resettingLeads() ? 'Resetting…' : 'Reset and queue contacts' }}</button>
          <button type="button" (click)="resetScope.set(null)" [disabled]="resettingLeads()">Cancel</button>
        </div>
        <p *ngIf="resetMessage()" role="status">{{ resetMessage() }}</p>
        <div class="lead-filters">
          <label>
            Status
            <select [value]="leadStatusFilter()" (change)="setLeadStatusFilter($event)">
              <option value="">All statuses</option>
              <option value="pending">pending</option>
              <option value="scheduled">scheduled</option>
              <option value="calling">calling</option>
              <option value="completed">completed</option>
              <option value="failed">failed</option>
              <option value="do_not_call">do_not_call</option>
            </select>
          </label>
          <label>
            Disposition
            <select [value]="leadDispositionFilter()" (change)="setLeadDispositionFilter($event)">
              <option value="">All tags</option>
              <option *ngFor="let d of dispositionOptions" [value]="d.id">{{ d.label }}</option>
            </select>
          </label>
        </div>
        <div class="disp-chips" *ngIf="dispositionSummary().length">
          <button
            type="button"
            class="chip"
            *ngFor="let d of dispositionSummary()"
            [attr.data-disp]="d.id"
            [class.active]="leadDispositionFilter() === d.id"
            (click)="toggleDisposition(d.id)"
            [disabled]="!d.count"
          >
            {{ d.label }} <strong>{{ d.count }}</strong>
          </button>
        </div>
        <div class="lead" *ngFor="let l of leads()" [attr.data-status]="normalizeStatus(l.status)">
          <input type="checkbox" [checked]="selectedLeadIds().has(l.id)" (change)="toggleLeadSelection(l.id, $event)" [attr.aria-label]="'Select ' + (l.name || l.phone)" [disabled]="l.status === 'calling' || resettingLeads()" />
          <span class="lead-main">
            <strong>{{ l.name || 'Unknown' }}</strong> · {{ l.phone }}
            <span class="status-pill" [attr.data-status]="normalizeStatus(l.status)">{{
              label(l.status)
            }}</span>
            <span class="tag" *ngIf="l.disposition" [attr.data-disp]="l.disposition">{{
              label(l.disposition)
            }}</span>
            <span *ngIf="l.scheduled_at"> · next {{ l.scheduled_at }}</span>
            <span> · {{ l.attempt_count }} attempts in current run</span>
            <span class="insight" *ngIf="leadInsight(l)" [title]="leadInsight(l)">
              · {{ leadInsight(l) }}</span
            >
          </span>
          <label class="gender-edit">
            Gender
            <select
              [value]="l.gender || 'unknown'"
              (change)="setLeadGender(l, $event)"
              [attr.data-gender]="l.gender || 'unknown'"
            >
              <option value="male">Male</option>
              <option value="female">Female</option>
              <option value="unknown">Unknown</option>
            </select>
          </label>
          <button type="button" class="ghost danger" (click)="removeLead(l.id)">Remove</button>
        </div>
        <p class="hint" *ngIf="!leads().length">
          No contacts match this filter (or none imported yet).
        </p>
      </section>

      <section class="card" [hidden]="step !== 0">
        <h3>CollectForm + PushFormApi</h3>
        <p class="help">
          <strong>1. CollectForm</strong> — list the field names the AI should ask for during the
          call (school name, contact person, city, etc.). The agent fills these while talking.<br />
          <strong>2. PushFormApi</strong> — when collection is done, the backend POSTs those values
          to the URL below using the JSON body template. If the API fails, data is saved in a silent
          outbox and retried (caller never hears an error).
        </p>

        <label>Form fields to collect (comma-separated)</label>
        <input
          [(ngModel)]="formFieldsCsv"
          (change)="syncFormFields()"
          placeholder="school_name, contact_name, phone, city, interest"
        />
        <p class="hint">Example: school_name, contact_name, phone, city, student_count, interest</p>

        <div class="row">
          <button type="button" class="ghost" (click)="useDemoFormEndpoint()">
            Use demo endpoint (saves as .txt)
          </button>
          <button type="button" class="ghost" (click)="fillSkooLAppExampleFields()">
            Fill SkooLApp example fields
          </button>
          <button type="button" (click)="runTestFormPush()" [disabled]="formTestBusy()">
            {{ formTestBusy() ? 'Pushing…' : 'Test form push (no phone call)' }}
          </button>
        </div>
        <p class="ok" *ngIf="formTestMsg()">{{ formTestMsg() }}</p>
        <p class="err" *ngIf="formTestErr()">{{ formTestErr() }}</p>

        <label>Push API URL</label>
        <input
          [(ngModel)]="c.form_api_url"
          placeholder="http://localhost:8000/api/v1/demo/form-inbox"
        />
        <label>Method</label>
        <input [(ngModel)]="c.form_api_method" placeholder="POST" />
        <label>Body template JSON</label>
        <textarea rows="8" [(ngModel)]="bodyTemplateText"></textarea>
        <p class="hint">
          Use double-curly placeholders that match field names, e.g.
          <code ngNonBindable>{{ school_name }}</code
          >, <code ngNonBindable>{{ phone }}</code
          >. Demo URL: <code>http://localhost:8000/api/v1/demo/form-inbox</code> — files land in
          <code>storage/form-inbox/</code> (under the backend folder). List submissions:
          <code>GET /api/v1/demo/form-inbox</code>.
        </p>
      </section>

      <div class="row">
        <button class="ghost" *ngIf="step > 0" (click)="step = step - 1">Back</button>
        <button *ngIf="step < 2" (click)="continueSetup()">Save & continue</button>
      </div>
    </div>
  `,
  styles: [
    `
      [hidden] {
        display: none !important;
      }
      [aria-current='step'] {
        outline: 2px solid #0f766e;
      }
      .page {
        display: grid;
        gap: 16px;
      }
      .top {
        display: flex;
        justify-content: space-between;
        gap: 12px;
      }
      h1,
      h3,
      h4 {
        margin: 0 0 8px;
        font-family: Georgia, serif;
      }
      .actions {
        display: flex;
        gap: 8px;
      }
      .grid {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 16px;
      }
      .card {
        background: white;
        border-radius: 16px;
        padding: 16px;
        display: grid;
        gap: 8px;
        box-shadow: 0 8px 24px rgba(15, 23, 42, 0.06);
      }
      input,
      select,
      textarea {
        border: 1px solid #cbd5e1;
        border-radius: 10px;
        padding: 10px;
        width: 100%;
        box-sizing: border-box;
      }
      button {
        border: 0;
        background: #0f766e;
        color: white;
        border-radius: 10px;
        padding: 10px 14px;
        cursor: pointer;
      }
      .ghost {
        background: #e2e8f0;
        color: #0f172a;
      }
      .flow {
        display: grid;
        gap: 8px;
      }
      .node {
        display: grid;
        grid-template-columns: 160px 1fr auto;
        gap: 8px;
      }
      .check {
        display: flex;
        gap: 8px;
        align-items: center;
      }
      .hint {
        color: #64748b;
        font-size: 13px;
      }
      .help {
        color: #334155;
        font-size: 14px;
        line-height: 1.45;
        background: #f8fafc;
        border: 1px solid #e2e8f0;
        border-radius: 10px;
        padding: 10px 12px;
      }
      .help code,
      .hint code {
        font-size: 12px;
        background: #e2e8f0;
        padding: 1px 4px;
        border-radius: 4px;
      }
      .ok {
        color: #047857;
      }
      .err {
        color: #b91c1c;
      }
      .row {
        display: flex;
        gap: 8px;
        align-items: center;
        flex-wrap: wrap;
      }
      .file-list,
      .lead {
        list-style: none;
        padding: 0;
        margin: 0;
      }
      .file-list li,
      .lead {
        display: flex;
        justify-content: space-between;
        align-items: center;
        gap: 8px;
        padding: 8px 0;
        border-bottom: 1px solid #e2e8f0;
      }
      .danger {
        background: #fee2e2;
        color: #991b1b;
      }
      .execution-head {
        display: flex;
        justify-content: space-between;
        align-items: start;
        gap: 12px;
      }
      .updated {
        color: #64748b;
        font-size: 12px;
      }
      .state-row {
        display: flex;
        flex-wrap: wrap;
        gap: 8px 14px;
        align-items: center;
        color: #475569;
        font-size: 13px;
      }
      .state {
        border-radius: 999px;
        padding: 4px 10px;
        background: #e2e8f0;
        font-weight: 700;
        text-transform: uppercase;
        font-size: 12px;
      }
      .state[data-state='running'],
      .state[data-state='ready'] {
        background: #d1fae5;
        color: #047857;
      }
      .state[data-state='blocked'],
      .state[data-state='attention'] {
        background: #fee2e2;
        color: #b91c1c;
      }
      .progress {
        height: 9px;
        background: #e2e8f0;
        border-radius: 99px;
        overflow: hidden;
      }
      .lead-reset-actions { display:flex; gap:12px; align-items:center; flex-wrap:wrap; margin:16px 0; }
      .lead-reset-actions label { display:flex; gap:8px; align-items:center; margin:0; }
      .lead-reset-actions input, .lead > input { width:16px; height:16px; flex-shrink:0; }
      .reset-review { background:#fffaf0; border:1px solid #ead7a5; border-radius:10px; padding:18px; margin:16px 0; }
      .progress span {
        display: block;
        height: 100%;
        background: #0f766e;
      }
      .call {
        display: flex;
        justify-content: space-between;
        gap: 8px;
        border-bottom: 1px solid #e2e8f0;
        padding: 8px 0;
        font-size: 13px;
      }
      .call-status {
        color: #475569;
      }
      .live-heading {
        display: flex;
        align-items: center;
        gap: 8px;
        margin: 14px 0 8px;
      }
      .pulse {
        width: 8px;
        height: 8px;
        border-radius: 50%;
        background: #14b8a6;
        box-shadow: 0 0 0 0 rgba(20, 184, 166, 0.55);
        animation: pulse 1.6s ease-out infinite;
      }
      @keyframes pulse {
        0% {
          box-shadow: 0 0 0 0 rgba(20, 184, 166, 0.55);
        }
        70% {
          box-shadow: 0 0 0 8px rgba(20, 184, 166, 0);
        }
        100% {
          box-shadow: 0 0 0 0 rgba(20, 184, 166, 0);
        }
      }
      .live-item {
        display: grid;
        gap: 6px;
        padding: 12px;
        margin: 8px 0;
        border-radius: 12px;
        border: 1px solid #99f6e4;
        border-left: 4px solid #14b8a6;
        background: #f0fdfa;
      }
      .live-item[data-phase='ai_speaking'] {
        border-left-color: #3b82f6;
        border-color: #bfdbfe;
        background: #f8fbff;
      }
      .live-item[data-phase='caller_speaking'] {
        border-left-color: #d97706;
        border-color: #fde68a;
        background: #fffbeb;
      }
      .live-item[data-phase='ringing'],
      .live-item[data-phase='dialing'] {
        border-left-color: #64748b;
        border-color: #cbd5e1;
        background: #f8fafc;
      }
      .live-item-head,
      .history-head {
        display: flex;
        justify-content: space-between;
        gap: 12px;
        align-items: flex-start;
      }
      .live-item-head strong,
      .history-head strong {
        display: block;
      }
      .live-item-head span,
      .history-head span {
        color: #64748b;
        font-size: 12px;
      }
      .live-item-aside,
      .history-aside {
        display: flex;
        flex-wrap: wrap;
        gap: 6px;
        align-items: center;
        justify-content: flex-end;
      }
      .phase {
        border-radius: 999px;
        padding: 3px 9px;
        font-size: 11px;
        font-weight: 700;
        background: #ecfdf5;
        color: #0f766e;
      }
      .phase[data-phase='ai_speaking'] {
        background: #dbeafe;
        color: #1d4ed8;
      }
      .phase[data-phase='caller_speaking'] {
        background: #fef3c7;
        color: #92400e;
      }
      .duration {
        font-variant-numeric: tabular-nums;
        font-weight: 600;
        font-size: 13px;
      }
      .snippet {
        margin: 0;
        font-size: 13px;
        color: #0f172a;
      }
      .history-item {
        display: grid;
        gap: 6px;
        padding: 12px;
        margin: 8px 0;
        border-radius: 12px;
        border: 1px solid #e2e8f0;
        border-left: 4px solid #94a3b8;
        background: #ffffff;
      }
      .history-item[data-status='completed'],
      .history-item[data-status='converted'],
      .history-item[data-status='interested'] {
        border-left-color: #22c55e;
        background: #f0fdf4;
        border-color: #bbf7d0;
      }
      .history-item[data-status='failed'],
      .history-item[data-status='do_not_call'],
      .history-item[data-status='not_interested'],
      .history-item[data-status='wrong_number'] {
        border-left-color: #ef4444;
        background: #fef2f2;
        border-color: #fecaca;
      }
      .history-item[data-status='started'],
      .history-item[data-status='calling'],
      .history-item[data-status='ringing'] {
        border-left-color: #3b82f6;
        background: #eff6ff;
        border-color: #bfdbfe;
      }
      .history-item[data-status='no_answer'],
      .history-item[data-status='busy'],
      .history-item[data-status='machine'],
      .history-item[data-status='voicemail'],
      .history-item[data-status='incomplete'],
      .history-item[data-status='no_response'] {
        border-left-color: #f59e0b;
        background: #fffbeb;
        border-color: #fde68a;
      }
      .history-item[data-status='scheduled'],
      .history-item[data-status='callback'],
      .history-item[data-status='customer_asked_later'] {
        border-left-color: #6366f1;
        background: #eef2ff;
        border-color: #c7d2fe;
      }
      .status-pill {
        border-radius: 999px;
        padding: 3px 9px;
        font-size: 11px;
        font-weight: 700;
        text-transform: uppercase;
        background: #e2e8f0;
        color: #334155;
      }
      .status-pill[data-status='completed'],
      .status-pill[data-status='converted'],
      .status-pill[data-status='interested'] {
        background: #dcfce7;
        color: #166534;
      }
      .status-pill[data-status='failed'],
      .status-pill[data-status='do_not_call'],
      .status-pill[data-status='not_interested'],
      .status-pill[data-status='wrong_number'] {
        background: #fee2e2;
        color: #991b1b;
      }
      .status-pill[data-status='started'],
      .status-pill[data-status='calling'],
      .status-pill[data-status='ringing'] {
        background: #dbeafe;
        color: #1d4ed8;
      }
      .status-pill[data-status='no_answer'],
      .status-pill[data-status='busy'],
      .status-pill[data-status='machine'],
      .status-pill[data-status='voicemail'],
      .status-pill[data-status='incomplete'],
      .status-pill[data-status='no_response'] {
        background: #ffedd5;
        color: #9a3412;
      }
      .status-pill[data-status='scheduled'],
      .status-pill[data-status='callback'],
      .status-pill[data-status='customer_asked_later'] {
        background: #e0e7ff;
        color: #3730a3;
      }
      .summary {
        margin: 0;
        font-size: 13px;
        color: #475569;
      }
      .blockers {
        margin: 0;
        padding-left: 18px;
        color: #9a3412;
        font-size: 14px;
      }
      button:disabled {
        opacity: 0.6;
        cursor: not-allowed;
      }
      @media (max-width: 900px) {
        .grid,
        .node {
          grid-template-columns: 1fr;
        }
      }
      .lead-filters {
        display: flex;
        flex-wrap: wrap;
        gap: 12px;
        margin: 8px 0 12px;
      }
      .lead-filters label {
        display: grid;
        gap: 4px;
        font-size: 12px;
        color: #475569;
      }
      .lead-filters select {
        min-width: 160px;
        padding: 8px 10px;
        border: 1px solid #cbd5e1;
        border-radius: 8px;
      }
      .disp-chips {
        display: flex;
        flex-wrap: wrap;
        gap: 6px;
        margin-bottom: 12px;
      }
      .chip {
        border: 1px solid #cbd5e1;
        background: #f8fafc;
        border-radius: 999px;
        padding: 4px 10px;
        font-size: 12px;
        cursor: pointer;
      }
      .chip[data-disp='converted'],
      .chip[data-disp='interested'],
      .tag[data-disp='converted'],
      .tag[data-disp='interested'] {
        background: #dcfce7;
        border-color: #86efac;
        color: #166534;
      }
      .chip[data-disp='callback'],
      .tag[data-disp='callback'] {
        background: #dbeafe;
        border-color: #93c5fd;
        color: #1d4ed8;
      }
      .chip[data-disp='not_interested'],
      .chip[data-disp='do_not_call'],
      .chip[data-disp='wrong_number'],
      .tag[data-disp='not_interested'],
      .tag[data-disp='do_not_call'],
      .tag[data-disp='wrong_number'] {
        background: #fee2e2;
        border-color: #fca5a5;
        color: #991b1b;
      }
      .chip[data-disp='incomplete'],
      .chip[data-disp='no_answer'],
      .chip[data-disp='busy'],
      .chip[data-disp='no_response'],
      .chip[data-disp='voicemail'],
      .tag[data-disp='incomplete'],
      .tag[data-disp='no_answer'],
      .tag[data-disp='busy'],
      .tag[data-disp='no_response'],
      .tag[data-disp='voicemail'] {
        background: #ffedd5;
        border-color: #fdba74;
        color: #9a3412;
      }
      .chip.active {
        background: #0f766e;
        border-color: #0f766e;
        color: white;
      }
      .tag {
        display: inline-block;
        margin-left: 6px;
        border-radius: 999px;
        padding: 2px 8px;
        font-size: 11px;
        border: 1px solid #e2e8f0;
        background: #f1f5f9;
      }
      .lead {
        border-left: 3px solid transparent;
        padding-left: 8px;
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: 10px;
      }
      .lead-main {
        flex: 1 1 220px;
      }
      .lead-main .insight {
        display: block;
        margin-top: 4px;
        font-size: 12px;
        color: #64748b;
        font-weight: 400;
      }
      .gender-edit {
        display: grid;
        gap: 2px;
        font-size: 11px;
        color: #475569;
      }
      .gender-edit select {
        min-width: 110px;
        padding: 6px 8px;
        border-radius: 8px;
        border: 1px solid #cbd5e1;
      }
      .gender-edit select[data-gender='male'] {
        border-color: #93c5fd;
        background: #eff6ff;
      }
      .gender-edit select[data-gender='female'] {
        border-color: #f9a8d4;
        background: #fdf2f8;
      }
      .gender-edit select[data-gender='unknown'] {
        border-color: #fcd34d;
        background: #fffbeb;
      }
      .lead[data-status='calling'],
      .lead[data-status='started'] {
        border-left-color: #3b82f6;
        background: #f8fbff;
      }
      .lead[data-status='completed'] {
        border-left-color: #22c55e;
      }
      .lead[data-status='failed'],
      .lead[data-status='do_not_call'] {
        border-left-color: #ef4444;
      }
      .lead[data-status='scheduled'] {
        border-left-color: #6366f1;
      }
      .chip:disabled {
        opacity: 0.45;
        cursor: default;
      }
    `,
  ],
})
export class CampaignDetailPage implements OnInit, OnDestroy {
  step = 0;
  statusBusy = false;
  campaign = signal<Campaign | null>(null);
  dataSources = signal<DataSource[]>([]);
  files = signal<Array<{ id: string; filename: string; chars: number }>>([]);
  leads = signal<Lead[]>([]);
  leadStatusFilter = signal('');
  selectedLeadIds = signal<Set<string>>(new Set());
  resetScope = signal<'all' | 'selected' | null>(null);
  resettingLeads = signal(false);
  resetMessage = signal('');
  leadDispositionFilter = signal('');
  dispositionSummary = signal<Array<{ id: string; label: string; count: number }>>([]);
  dispositionOptions = [
    { id: 'completed', label: 'Conversation completed' },
    { id: 'transferred', label: 'Transferred to team' },
    { id: 'converted', label: 'Converted' },
    { id: 'interested', label: 'Interested' },
    { id: 'not_interested', label: 'Not interested' },
    { id: 'callback', label: 'Callback / later' },
    { id: 'wrong_number', label: 'Wrong number' },
    { id: 'do_not_call', label: 'Do not call' },
    { id: 'no_answer', label: 'No answer' },
    { id: 'busy', label: 'Busy' },
    { id: 'no_response', label: 'Silent / no response' },
    { id: 'voicemail', label: 'Voicemail / machine' },
    { id: 'incomplete', label: 'No clear conclusion' },
    { id: 'audio_problem', label: 'Audio / communication problem' },
    { id: 'connection_failed', label: 'Connection failed' },
    { id: 'interrupted', label: 'Conversation interrupted' },
  ];
  csvHeaders = signal<string[]>([]);
  csvFile: File | null = null;
  phoneCol = '';
  nameCol = '';
  addressCol = '';
  genderCol = '';
  insightsCol = '';
  formFieldsCsv = '';
  bodyTemplateText = '{}';
  importMsg = signal('');
  importing = signal(false);
  saved = signal(false);
  error = signal('');
  queueLoading = signal(false);
  queueSummary = signal('');
  queueBlockers = signal<string[]>([]);
  operations = signal<OperationsStatus | null>(null);
  formTestBusy = signal(false);
  formTestMsg = signal('');
  formTestErr = signal('');
  nodeTypes = NODE_TYPES;
  private refreshTimer?: ReturnType<typeof setInterval>;

  constructor(
    private readonly route: ActivatedRoute,
    private readonly api: ApiService,
    private readonly auth: AuthService,
  ) {}

  isAdmin(): boolean {
    return this.auth.user()?.role === 'admin';
  }

  /** Short CRM insight text stored under custom_data_json.insights (or notes). */
  leadInsight(lead: Lead): string {
    const data = lead.custom_data_json || {};
    const raw = (data['insights'] || data['notes'] || data['additional_info'] || '')
      .toString()
      .trim();
    if (!raw) return '';
    return raw.length > 80 ? raw.slice(0, 77) + '…' : raw;
  }

  label(value: string): string {
    return (value || '').replaceAll('_', ' ');
  }

  normalizeStatus(value: string | null | undefined): string {
    return (value || 'unknown').trim().toLowerCase().replace(/\s+/g, '_');
  }

  formatDuration = formatDuration;
  completionLabel = completionLabel;

  async ngOnInit(): Promise<void> {
    this.step = this.route.snapshot.queryParamMap.has('setup') ? 0 : 2;
    const id = this.route.snapshot.paramMap.get('id')!;
    const [c, ds] = await Promise.all([this.api.getCampaign(id), this.api.listDataSources()]);
    this.campaign.set(c);
    this.dataSources.set(ds);
    this.formFieldsCsv = (c.form_schema_json || []).map((f) => f.name).join(', ');
    this.bodyTemplateText = JSON.stringify(c.form_api_body_template || {}, null, 2);
    this.files.set(await this.api.listFiles(id));
    if (c.campaign_type === 'outbound') {
      await this.reloadLeads();
    }
    await this.refreshExecution();
    this.refreshTimer = setInterval(() => void this.refreshExecution(true), 5000);
  }

  ngOnDestroy(): void {
    if (this.refreshTimer) clearInterval(this.refreshTimer);
  }

  async reloadLeads(): Promise<void> {
    const c = this.campaign();
    if (!c || c.campaign_type !== 'outbound') return;
    const [leads, summary] = await Promise.all([
      this.api.listLeads(c.id, {
        status: this.leadStatusFilter() || undefined,
        disposition: this.leadDispositionFilter() || undefined,
      }),
      this.api.dispositionSummary(c.id),
    ]);
    this.leads.set(leads);
    this.selectedLeadIds.set(new Set([...this.selectedLeadIds()].filter(id => leads.some(lead => lead.id === id))));
    this.dispositionSummary.set(summary.dispositions.filter((d) => d.count > 0));
  }

  setLeadStatusFilter(event: Event): void {
    const value = (event.target as HTMLSelectElement).value;
    this.leadStatusFilter.set(value);
    void this.reloadLeads();
  }

  toggleLeadSelection(id: string, event: Event) {
    const next = new Set(this.selectedLeadIds());
    (event.target as HTMLInputElement).checked ? next.add(id) : next.delete(id);
    this.selectedLeadIds.set(next);
  }
  allVisibleSelected() {
    const eligible = this.leads().filter(lead => lead.status !== 'calling');
    return !!eligible.length && eligible.every(lead => this.selectedLeadIds().has(lead.id));
  }
  selectVisibleLeads(event: Event) {
    this.selectedLeadIds.set((event.target as HTMLInputElement).checked
      ? new Set(this.leads().filter(lead => lead.status !== 'calling').map(lead => lead.id)) : new Set());
  }
  openReset(scope: 'all' | 'selected') { this.resetMessage.set(''); this.resetScope.set(scope); }
  async confirmLeadReset() {
    const campaign = this.campaign(), scope = this.resetScope();
    if (!campaign || !scope || this.resettingLeads()) return;
    this.resettingLeads.set(true);
    try {
      const result = await this.api.resetLeads(campaign.id, scope, scope === 'selected' ? [...this.selectedLeadIds()] : []);
      this.resetMessage.set(`${result.reset_count} contacts reset. ${result.skipped_active} active and ${result.skipped_suppressed} suppressed contacts skipped. Call history preserved.`);
      this.selectedLeadIds.set(new Set()); this.resetScope.set(null);
      await this.reloadLeads(); await this.refreshExecution();
    } catch (error: any) { this.resetMessage.set(error?.error?.detail || 'Could not reset contacts. Please refresh and try again.'); }
    finally { this.resettingLeads.set(false); }
  }

  setLeadDispositionFilter(event: Event): void {
    const value = (event.target as HTMLSelectElement).value;
    this.leadDispositionFilter.set(value);
    void this.reloadLeads();
  }

  toggleDisposition(id: string): void {
    this.leadDispositionFilter.set(this.leadDispositionFilter() === id ? '' : id);
    void this.reloadLeads();
  }

  operation(): CampaignOperation | undefined {
    const id = this.campaign()?.id;
    return id ? this.operations()?.campaigns.find((item) => item.campaign_id === id) : undefined;
  }

  addNode(): void {
    const c = this.campaign();
    if (!c) return;
    const node: FlowNode = {
      id: `n${Date.now()}`,
      type: 'SpeakScript',
      label: 'New step',
      data: {},
    };
    c.flow_json.nodes = [...(c.flow_json.nodes || []), node];
    this.campaign.set({ ...c });
  }

  removeNode(i: number): void {
    const c = this.campaign();
    if (!c) return;
    c.flow_json.nodes = c.flow_json.nodes.filter((_, idx) => idx !== i);
    this.campaign.set({ ...c });
  }

  syncFormFields(): void {
    const c = this.campaign();
    if (!c) return;
    c.form_schema_json = this.formFieldsCsv
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean)
      .map((name) => ({ name, label: name }));
  }

  async save(): Promise<boolean> {
    const c = this.campaign();
    if (!c) return false;
    this.error.set('');
    this.saved.set(false);
    this.syncFormFields();
    try {
      c.form_api_body_template = JSON.parse(this.bodyTemplateText || '{}');
    } catch {
      this.error.set('Body template must be valid JSON');
      return false;
    }
    try {
      const updated = await this.api.updateCampaign(c.id, c);
      this.campaign.set(updated);
      this.saved.set(true);
      return true;
    } catch (e: any) {
      this.error.set(e?.error?.detail || 'Save failed');
      return false;
    }
  }

  async setStatus(status: string): Promise<void> {
    const c = this.campaign();
    if (!c || this.statusBusy) return;
    this.statusBusy = true;
    this.error.set('');
    try {
      if (status === 'active') {
        if (!c.script_text.trim()) {
          this.error.set('Add a script in step 1 before starting.');
          return;
        }
        if (c.campaign_type === 'outbound' && !this.leads().length) {
          this.error.set('Import contacts in step 2 before starting.');
          return;
        }
        if (!(await this.save())) return;
      }
      const updated = await this.api.updateCampaign(c.id, { status } as Partial<Campaign>);
      this.campaign.set(updated);
      await this.refreshExecution();
    } catch (e: any) {
      this.error.set(e?.error?.detail || 'Could not change campaign status. Please try again.');
    } finally {
      this.statusBusy = false;
    }
  }

  async continueSetup(): Promise<void> {
    if (await this.save()) {
      this.step = Math.min(2, this.step + 1);
      await this.refreshExecution();
    }
  }

  async onFile(ev: Event): Promise<void> {
    const input = ev.target as HTMLInputElement;
    const file = input.files?.[0];
    const c = this.campaign();
    if (!file || !c) return;
    await this.api.uploadFile(c.id, file);
    this.files.set(await this.api.listFiles(c.id));
    input.value = '';
  }

  async removeFile(fileId: string): Promise<void> {
    const c = this.campaign();
    if (!c) return;
    await this.api.deleteFile(c.id, fileId);
    this.files.set(await this.api.listFiles(c.id));
  }

  onCsv(ev: Event): void {
    const input = ev.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;
    this.csvFile = file;
    this.importMsg.set('');
    const reader = new FileReader();
    reader.onload = () => {
      const text = String(reader.result || '');
      const header = text.split(/\r?\n/)[0] || '';
      const cols = header.split(',').map((x) => x.trim().replace(/^"|"$/g, ''));
      this.csvHeaders.set(cols);
      this.phoneCol = cols.find((c) => /phone|mobile|number/i.test(c)) || cols[0] || '';
      this.nameCol = cols.find((c) => /name/i.test(c)) || '';
      this.addressCol = cols.find((c) => /address|city/i.test(c)) || '';
      this.genderCol = cols.find((c) => /gender|sex|جنسیت/i.test(c)) || '';
      this.insightsCol =
        cols.find((c) => /insight|additional|notes|remark|comment|about|detail|info/i.test(c)) ||
        '';
    };
    reader.readAsText(file);
  }

  clearCsv(input?: HTMLInputElement): void {
    this.csvFile = null;
    this.csvHeaders.set([]);
    this.phoneCol = '';
    this.nameCol = '';
    this.addressCol = '';
    this.genderCol = '';
    this.insightsCol = '';
    this.importMsg.set('');
    if (input) input.value = '';
  }

  async importCsv(input?: HTMLInputElement): Promise<void> {
    const c = this.campaign();
    if (!c || !this.csvFile || this.importing()) return;
    this.importing.set(true);
    this.importMsg.set('');
    this.error.set('');
    try {
      const extra: Record<string, string> = {};
      if (this.insightsCol) {
        extra['insights'] = this.insightsCol;
      }
      const res = await this.api.importCsv(c.id, this.csvFile, {
        phone_column: this.phoneCol,
        name_column: this.nameCol || null,
        address_column: this.addressCol || null,
        gender_column: this.genderCol || null,
        extra_columns: extra,
      });
      this.importMsg.set(
        `Imported ${res.imported} leads` +
          (res.skipped_duplicates ? ` · skipped ${res.skipped_duplicates} duplicates` : '') +
          (res.missing_gender ? ` · ${res.missing_gender} without gender (set below)` : '') +
          (this.insightsCol ? ` · insights from “${this.insightsCol}”` : ''),
      );
      await this.reloadLeads();
      this.clearCsv(input);
    } catch (e: any) {
      this.error.set(e?.error?.detail || 'Import failed');
    } finally {
      this.importing.set(false);
    }
  }

  async removeLead(leadId: string): Promise<void> {
    const c = this.campaign();
    if (!c) return;
    this.error.set('');
    try {
      await this.api.deleteLead(c.id, leadId);
      await this.reloadLeads();
    } catch (e: any) {
      this.error.set(e?.error?.detail || 'Could not delete lead');
      await this.reloadLeads();
    }
  }

  async setLeadGender(lead: Lead, event: Event): Promise<void> {
    const c = this.campaign();
    if (!c) return;
    const gender = (event.target as HTMLSelectElement).value;
    try {
      const updated = await this.api.updateLead(c.id, lead.id, { gender });
      this.leads.update((rows) =>
        rows.map((row) => (row.id === lead.id ? { ...row, ...updated } : row)),
      );
    } catch (e: any) {
      this.error.set(e?.error?.detail || 'Could not save lead gender');
      await this.reloadLeads();
    }
  }

  async refreshQueue(): Promise<void> {
    const c = this.campaign();
    if (!c || this.queueLoading()) return;
    this.queueLoading.set(true);
    this.queueSummary.set('');
    this.queueBlockers.set([]);
    try {
      const res = await this.api.queueStatus(c.id);
      this.queueSummary.set(res.summary);
      const mine = res.campaigns.find((x) => x.campaign_id === c.id);
      this.queueBlockers.set(mine?.blockers || []);
    } catch (e: any) {
      this.queueSummary.set(e?.error?.detail || 'Could not load queue status');
    } finally {
      this.queueLoading.set(false);
    }
  }

  async refreshExecution(silent = false): Promise<void> {
    const c = this.campaign();
    if (!c || this.queueLoading()) return;
    if (!silent) this.queueLoading.set(true);
    try {
      this.operations.set(await this.api.operations(c.id));
    } catch (e: any) {
      if (!silent) this.error.set(e?.error?.detail || 'Could not load execution status');
    } finally {
      this.queueLoading.set(false);
    }
  }

  async runTestFormPush(): Promise<void> {
    const c = this.campaign();
    if (!c || this.formTestBusy()) return;
    this.formTestBusy.set(true);
    this.formTestMsg.set('');
    this.formTestErr.set('');
    try {
      // Persist URL/template first so the test uses current form settings
      if (!(await this.save())) return;
      const res = await this.api.testFormPush(c.id);
      this.formTestMsg.set(
        `${res.message} → ${res.target_url}` + (res.queued ? ' (queued/outbox)' : ''),
      );
    } catch (e: any) {
      this.formTestErr.set(e?.error?.detail || 'Test form push failed');
    } finally {
      this.formTestBusy.set(false);
    }
  }

  onNodeTypeChange(n: FlowNode): void {
    if (n.type === 'CollectForm' && !n.label) n.label = 'Collect form fields';
    if (n.type === 'PushFormApi' && !n.label) n.label = 'Push form to API';
  }

  addFormFlowExample(): void {
    const c = this.campaign();
    if (!c) return;
    const stamp = Date.now();
    const nodes = [...(c.flow_json.nodes || [])];
    if (!nodes.some((n) => n.type === 'CollectForm')) {
      nodes.push({
        id: `n${stamp}_collect`,
        type: 'CollectForm',
        label: 'Ask for school details',
        data: {},
      });
    }
    if (!nodes.some((n) => n.type === 'PushFormApi')) {
      nodes.push({
        id: `n${stamp}_push`,
        type: 'PushFormApi',
        label: 'Send form to API',
        data: {},
      });
    }
    c.flow_json = { ...c.flow_json, nodes };
    this.campaign.set({ ...c });
    if (!this.formFieldsCsv.trim()) this.fillSkooLAppExampleFields();
    if (!(c.form_api_url || '').trim()) this.useDemoFormEndpoint();
  }

  fillSkooLAppExampleFields(): void {
    this.formFieldsCsv =
      'school_name, contact_name, phone, city, student_count, interest, preferred_demo_time';
    this.syncFormFields();
    this.bodyTemplateText = JSON.stringify(
      {
        source: 'microkosm-voice',
        campaign: 'skoolapp-outbound',
        school_name: '{{school_name}}',
        contact_name: '{{contact_name}}',
        phone: '{{phone}}',
        city: '{{city}}',
        student_count: '{{student_count}}',
        interest: '{{interest}}',
        preferred_demo_time: '{{preferred_demo_time}}',
      },
      null,
      2,
    );
  }

  useDemoFormEndpoint(): void {
    const c = this.campaign();
    if (!c) return;
    c.form_api_url = 'http://localhost:8000/api/v1/demo/form-inbox';
    c.form_api_method = 'POST';
    if (!this.formFieldsCsv.trim()) this.fillSkooLAppExampleFields();
    else if (!this.bodyTemplateText || this.bodyTemplateText.trim() === '{}') {
      const fields = this.formFieldsCsv
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);
      const body: Record<string, string> = { source: 'microkosm-voice' };
      for (const f of fields) body[f] = `{{${f}}}`;
      this.bodyTemplateText = JSON.stringify(body, null, 2);
    }
    this.campaign.set({ ...c });
  }
}
